import { useState } from "react";
import { EventDetail } from "@/mocks/eventDetails";

interface Props {
  event: EventDetail;
}

const FORM_URL = "https://readdy.ai/api/form/d7bvfjmhe76khnho4s7g";

export default function EventRegistrationForm({ event }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [count, setCount] = useState("1");
  const [note, setNote] = useState("");
  const [noteLen, setNoteLen] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 500) {
      setNote(e.target.value);
      setNoteLen(e.target.value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError("請填寫所有必填欄位");
      return;
    }
    setError("");
    setSubmitting(true);

    const formData = new URLSearchParams();
    formData.append("event_title", event.title);
    formData.append("event_date", event.date + (event.endDate ? ` ~ ${event.endDate}` : ""));
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("count", count);
    formData.append("note", note);

    try {
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="registration-form" className="py-16 bg-stone-50">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-red-600 text-sm font-semibold tracking-widest uppercase mb-2">Registration</p>
          <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Noto Serif TC', serif" }}>
            立即報名
          </h2>
          <p className="text-gray-500 text-sm mt-2">填寫以下資料，紅歡密團隊將盡快與你確認</p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
              報名成功！
            </h3>
            <p className="text-gray-500 text-base mb-6">
              感謝你報名「{event.title}」！<br />
              紅歡密團隊將在 24 小時內透過 Email 或電話與你確認。
            </p>
            <a
              href={event.contactLine}
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500 text-white font-semibold text-sm hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-line-fill" />
              加入 LINE 取得最新消息
            </a>
          </div>
        ) : (
          <form
            data-readdy-form
            id="event-registration-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-8 border border-gray-100 flex flex-col gap-5"
          >
            {/* Event Info Banner */}
            <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-600 shrink-0">
                <i className="ri-calendar-event-line text-white text-base" />
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">{event.title}</p>
                <p className="text-gray-500 text-xs">
                  {event.date}{event.endDate ? ` ～ ${event.endDate}` : ""} ｜ {event.location}
                </p>
              </div>
              <span className="ml-auto text-red-600 font-bold text-sm whitespace-nowrap">{event.priceLabel}</span>
            </div>

            <input type="hidden" name="event_title" value={event.title} />
            <input type="hidden" name="event_date" value={event.date + (event.endDate ? ` ~ ${event.endDate}` : "")} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="請輸入你的姓名"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  電話 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="請輸入聯絡電話"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="請輸入 Email（確認信將寄至此信箱）"
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">報名人數</label>
              <select
                name="count"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors bg-white cursor-pointer"
              >
                {[1,2,3,4,5,6].map((n) => (
                  <option key={n} value={n}>{n} 人</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">備註（選填）</label>
              <textarea
                name="note"
                value={note}
                onChange={handleNoteChange}
                placeholder="有任何特殊需求或想告訴我們的事..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors resize-none"
              />
              <p className={`text-xs text-right ${noteLen >= 500 ? "text-red-500" : "text-gray-400"}`}>
                {noteLen}/500
              </p>
            </div>

            {error && (
              <p className="text-red-500 text-sm flex items-center gap-1.5">
                <i className="ri-error-warning-line" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-full bg-red-600 text-white font-bold text-base hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  送出中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" />
                  確認報名
                </>
              )}
            </button>

            <p className="text-gray-400 text-xs text-center">
              送出後，紅歡密團隊將在 24 小時內與你確認報名資訊
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
